import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import * as StackTrace from 'stacktrace-js';
import { ErrorReportingService } from '../services/error-reporting/error-reporting.service';
import { UpdateNotifierService } from '../services/update/update-notifier.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private errorReportingService: ErrorReportingService,
    private zone: NgZone,
    private updateNotifierService: UpdateNotifierService
  ) {}

  private errorCache: Map<string, { count: number; timestamp: number }> =
    new Map();
  private readonly ERROR_THRESHOLD = 3;
  private readonly TIME_WINDOW = 10000;

  excludedErrors = ['NG0100', 'Http failure'];

  handleError(error: any): void {
    console.error(error);

    const errorMessage = this.getErrorMessage(error);

    if (errorMessage.includes('Failed to fetch dynamically imported module')) {
      this.updateNotifierService.setUpdateAvailable(true);
      return;
    }

    if (this.excludedErrors.some(code => errorMessage.includes(code))) {
      return;
    }

    // We do this check for errors that are re-triggered via change detection calls to prevent getting stuck in loop
    if (this.shouldThrottleError(errorMessage)) {
      console.warn(
        `Error "${errorMessage}" occurred too frequently. Throttling.`
      );
      return;
    }

    this.zone.runOutsideAngular(() => {
      StackTrace.fromError(error)
        .then(frames => {
          const mappedStack = frames.map(f => f.toString()).join('\n');
          this.errorReportingService.reportError(errorMessage, mappedStack);
        })
        .catch(stackError => {
          console.error('Failed to generate stack trace:', stackError);
          this.errorReportingService.reportError(
            errorMessage,
            'Stack trace unavailable'
          );
        });
    });
  }

  private getErrorMessage(error: any): string {
    if (!error) return 'Unknown error';
    if (typeof error === 'string') return error;
    return error.message || String(error);
  }

  private shouldThrottleError(errorMessage: string): boolean {
    const now = Date.now();
    const errorKey = this.normaliseErrorMessage(errorMessage);

    this.cleanupErrorCache(now);

    const errorEntry = this.errorCache.get(errorKey);

    if (!errorEntry) {
      this.errorCache.set(errorKey, { count: 1, timestamp: now });
      return false;
    }

    errorEntry.count++;
    errorEntry.timestamp = now;

    return errorEntry.count > this.ERROR_THRESHOLD;
  }

  private normaliseErrorMessage(message: string): string {
    return message.trim().substring(0, 100);
  }

  private cleanupErrorCache(currentTime: number): void {
    for (const [key, entry] of this.errorCache.entries()) {
      if (currentTime - entry.timestamp > this.TIME_WINDOW) {
        this.errorCache.delete(key);
      }
    }
  }
}
